// Changelog parsing and release-note visibility rules.
//
// Kept free of Angular DI so it runs under plain node in unit tests — the same
// split `text-locale.ts` and `faq-sections.ts` use. `ChangelogPage` is a thin
// caller over these.
//
// See design-docs/global-common-localization.md.

export interface ChangelogSection {
  /** `null` for the banner block that sits before the first version heading. */
  title: string | null;
  /** ISO date parsed out of the heading, e.g. "2026-09-19". */
  date: string | null;
  content: string;
  /** True when this entry is for a version newer than the one running. */
  comingSoon: boolean;
}

/** `[major, minor, patch]`, or `null` when the string is not a version. */
export function parseVersion(str: string): number[] | null {
  const match = str.match(/^v?(\d+)\.(\d+)\.(\d+)/);
  if (!match) {
    return null;
  }
  return [parseInt(match[1]), parseInt(match[2]), parseInt(match[3])];
}

/** True when version `a` is strictly newer than version `b`. */
export function isVersionNewer(a: number[], b: number[]): boolean {
  for (let i = 0; i < 3; i++) {
    if (a[i] !== b[i]) {
      return a[i] > b[i];
    }
  }
  return false;
}

/**
 * Split the changelog markdown into its banner and its version entries.
 *
 * The banner is everything before the first `# vX.Y.Z` heading and comes back
 * with a `null` title.
 */
export function parseChangelog(text: string): ChangelogSection[] {
  const sections: ChangelogSection[] = [];

  // split on level-1 headings while keeping the heading with its content
  const parts = (text || '').split(/^(?=# [^#])/m);

  for (const part of parts) {
    const trimmed = part.trim();
    if (!trimmed) {
      continue;
    }

    // if this part opens with a level-1 heading it is a version entry
    if (trimmed.startsWith('# ')) {
      const firstNewline = trimmed.indexOf('\n');
      const titleLine = trimmed.substring(2, firstNewline > 0 ? firstNewline : undefined).trim();
      const content = firstNewline > 0 ? trimmed.substring(firstNewline + 1).trim() : '';

      // headings carry an optional date: "v0.9.x.x (YYYY-MM-DD)"
      const dateMatch = titleLine.match(/^(.+?)\s+\((\d{4}-\d{2}-\d{2})\)$/);
      const title = dateMatch ? dateMatch[1] : titleLine;
      const date = dateMatch ? dateMatch[2] : null;

      sections.push({ title, date, content, comingSoon: false });
    } else {
      // content before the first heading — the announcements banner
      sections.push({ title: null, date: null, content: trimmed, comingSoon: false });
    }
  }

  return sections;
}

/**
 * Decide which sections the user actually sees, given the version they run.
 *
 * Two rules apply, in order:
 *
 *  - **Collapse patches.** Within one `major.minor` group only the newest patch
 *    and the patch the user is running are kept, so a long run of bug-fix
 *    releases does not bury the interesting entries.
 *  - **Hide what they do not have.** Entries for a version newer than the one
 *    running are marked `comingSoon` and dropped, so a freshly published entry
 *    cannot show notes for a build the user has not received yet.
 *
 * **The escape hatch:** if those rules would leave NO release notes at all, the
 * newer entries are shown after all. That happens when the published changelog
 * starts above the version the user runs — which became reachable when the
 * published changelog was trimmed to a sliding window of recent releases
 * (#4210). Such a user would otherwise get a page holding only the banner,
 * reached by tapping a "What's new" card that fired because the document
 * changed. Showing what they will get when they update beats showing nothing,
 * and the hide rule still applies in every case where the user has notes of
 * their own — which is where that rule actually earns its keep.
 *
 * Mutates each section's `comingSoon` flag, so callers can still tell which
 * entries are ahead of the running version.
 */
export function filterAndMarkSections(
  sections: ChangelogSection[],
  appVersion: string,
): ChangelogSection[] {
  const currentVersion = parseVersion(appVersion);
  // if the running version cannot be parsed there is nothing to compare against
  if (!currentVersion) {
    return sections;
  }

  for (const section of sections) {
    if (!section.title) {
      continue;
    }
    const version = parseVersion(section.title);
    if (version) {
      section.comingSoon = isVersionNewer(version, currentVersion);
    }
  }

  // group the versioned entries by major.minor so patches can be collapsed
  const minorKey = (v: number[]) => `${v[0]}.${v[1]}`;
  const groups = new Map<string, ChangelogSection[]>();
  for (const section of sections) {
    if (!section.title) {
      continue;
    }
    const version = parseVersion(section.title);
    if (!version) {
      continue;
    }
    const key = minorKey(version);
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key).push(section);
  }

  const hiddenSections = new Set<ChangelogSection>();
  for (const group of groups.values()) {
    if (group.length <= 1) {
      continue;
    }
    const kept = new Set<ChangelogSection>();
    // the changelog is newest-first, so the first entry is the newest patch
    kept.add(group[0]);
    // and keep the patch the user is actually running
    for (const section of group) {
      const v = parseVersion(section.title);
      if (v[0] === currentVersion[0] && v[1] === currentVersion[1] && v[2] === currentVersion[2]) {
        kept.add(section);
      }
    }
    for (const section of group) {
      if (!kept.has(section)) {
        hiddenSections.add(section);
      }
    }
  }

  const visible = sections.filter(s => !hiddenSections.has(s) && !s.comingSoon);

  // If the rules above left no RELEASE notes at all, show the newer ones anyway.
  //
  // "Release notes" means sections with a parseable version, not merely a title.
  // `parseChangelog` gives a title to every level-1 heading, so counting titles
  // would let one plain `# Announcements` heading in the published changelog
  // count as "the user has something to read" and silently switch every
  // below-the-window user back to a page with no release notes at all.
  const isRelease = (section: ChangelogSection) =>
    section.title !== null && parseVersion(section.title) !== null;
  if (!visible.some(isRelease) && sections.some(isRelease)) {
    return sections.filter(s => !hiddenSections.has(s));
  }
  return visible;
}
